import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type GlobalDataState from '../types/GlobalDataState';
import UserEntity from '@/app/features/user/entities/UserEntity';
import LivestreamProductEntity from '@/livestream/features/livestream-product/entities/LivestreamProductEntity';

const initialState: GlobalDataState = {
    isCanFetchData: false,
    isAlreadyFetchData: false,
    customerSourceId: null,
    qrDiscountPercent: 0,
    customerInfo: null,
    products: [],
    isLivestreamSessionExpired: false,
    isTooManyRequests: false,
    isFetchError: false,
};

export const GlobalDataSliceName = 'globalData';
export const GlobalDataSlice = createSlice({
    name: GlobalDataSliceName,
    initialState,
    reducers: {
        canFetchData: (state) => {
            state.isCanFetchData = true;
        },
        canNotFetchData: (state) => {
            state.isCanFetchData = false;
        },
        markAsFetchedData: (state) => {
            state.isAlreadyFetchData = true;
        },
        setCustomerSourceId: (state, action: PayloadAction<number | null>) => {
            const value = action.payload;
            if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
                state.customerSourceId = Math.floor(value);
                return;
            }
            state.customerSourceId = null;
        },
        setQrDiscountPercent: (state, action: PayloadAction<number>) => {
            const value = Math.floor(Number(action.payload));
            if (!Number.isFinite(value) || value < 0) {
                state.qrDiscountPercent = 0;
                return;
            }
            state.qrDiscountPercent = value > 100 ? 100 : value;
        },
        setCustomerInfo: (state, action: PayloadAction<UserEntity | null>) => {
            state.customerInfo = action.payload;
        },
        setProducts: (state, action: PayloadAction<LivestreamProductEntity[]>) => {
            state.products = action.payload;
        },
        setIsLivestreamSessionExpired: (state, action: PayloadAction<boolean>) => {
            state.isLivestreamSessionExpired = action.payload;
        },
        setIsTooManyRequests: (state, action: PayloadAction<boolean>) => {
            state.isTooManyRequests = action.payload === true;
        },
        setIsFetchError: (state, action: PayloadAction<boolean>) => {
            state.isFetchError = action.payload === true;
        },
    },
});

export const {
    canFetchData,
    canNotFetchData,
    markAsFetchedData,
    setCustomerSourceId,
    setQrDiscountPercent,
    setCustomerInfo,
    setProducts,
    setIsLivestreamSessionExpired,
    setIsTooManyRequests,
    setIsFetchError,
} = GlobalDataSlice.actions;

export const GlobalDataReducer = GlobalDataSlice.reducer;
